import { useState } from 'react';
import type { ReactElement } from 'react';
import { useShoppingList } from '../hooks/useShoppingList.js';
import { defaultRange, formatRange } from './shopping/rangeUtils.js';
import { RangePicker } from './shopping/RangePicker.js';
import { ProgressBar } from './shopping/ProgressBar.js';
import { ItemList } from './shopping/ItemList.js';
import { AddItemForm } from './shopping/AddItemForm.js';
import { StatusPill } from './shopping/StatusPill.js';
import { PdfButton } from './shopping/PdfButton.js';

export function Shopping(): ReactElement {
  const shop = useShoppingList();
  const initial = defaultRange();
  const [startDate, setStartDate] = useState<string>(initial.start_date);
  const [endDate, setEndDate] = useState<string>(initial.end_date);
  const [generating, setGenerating] = useState<boolean>(false);

  async function handleGenerate(): Promise<void> {
    if (!startDate || !endDate) return;
    setGenerating(true);
    try {
      await shop.generate({ start_date: startDate, end_date: endDate });
    } finally {
      setGenerating(false);
    }
  }

  const rangeLabel =
    shop.list && shop.list.start_date
      ? formatRange(shop.list.start_date, shop.list.end_date)
      : formatRange(startDate, endDate);

  return (
    <section
      className="flex h-full flex-col"
      data-testid="shopping-view"
    >
      <div
        className="flex flex-col gap-3 p-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }}
      >
        <header className="flex items-center gap-2">
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--text-main)' }}
            data-testid="shopping-title"
          >
            Shopping
          </h2>
          <div className="ml-auto">
            <StatusPill status={shop.sync.status} pending={shop.sync.pending} />
          </div>
        </header>

        <RangePicker
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
          onGenerate={() => void handleGenerate()}
          generating={generating}
        />

        <div className="flex flex-col gap-1">
          <div
            className="text-sm"
            style={{ color: 'var(--text-muted)' }}
            data-testid="shopping-range-label"
          >
            {rangeLabel}
          </div>
          <ProgressBar completed={shop.progress.completed} total={shop.progress.total} />
        </div>

        <div className="flex gap-2">
          <PdfButton listId={shop.list?.list_id ?? null} />
          <button
            type="button"
            onClick={() => void shop.clearAll()}
            disabled={!shop.list || shop.list.items.length === 0}
            className="flex min-h-[40px] items-center rounded-md border px-3 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'transparent',
            }}
            data-testid="shopping-clear"
          >
            🗑️ Clear all
          </button>
        </div>

        {shop.error ? (
          <div
            className="rounded-md border p-3 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'color-mix(in srgb, #dc2626 8%, transparent)',
            }}
            data-testid="shopping-error"
            role="alert"
          >
            {shop.error}
          </div>
        ) : null}

        <ItemList
          items={shop.list?.items ?? []}
          onToggle={(id, value) => void shop.toggle(id, value)}
          onRemove={(id) => void shop.removeItem(id)}
          onReorder={(id, targetIndex) => void shop.reorderItem(id, targetIndex)}
        />

        <AddItemForm
          disabled={shop.list === null}
          listId={shop.list?.list_id ?? 0}
          onSubmit={async (req) => {
            await shop.addItem(req);
          }}
        />
      </div>
    </section>
  );
}
